import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faFilter, 
  faDownload, 
  faEye,
  faEdit,
  faTrash,
  faPaperPlane,
  faCalendarAlt,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faEllipsisV,
  faRefresh,
  faUpload,
  faChartBar,
  faSort,
  faSortUp,
  faSortDown,
  faTimesCircle,
  faSpinner,
  faFileExcel,
  faTimes,
  faClipboardList,
  faBell,
  faCheck,
  faEnvelope,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { 
  getSubmissions, 
  getSubmissionStats, 
  getDeadlineStats,
  exportSubmissions,
  bulkSendQuestionnaires,
  updateSubmissionStatus,
  addInternalNote,
  deleteSubmission,
  sendManualReminder,
  bulkDeleteSubmissions
} from '../services/submissionApi';
import { getQuestionnaireTemplates, getSuppliers } from '../services/api';

import { Submission, SubmissionFilters, SubmissionStats, DeadlineStats } from '../types/submission';
import { useToast } from '../components/ui/ToastProvider';
import BulkSendModal from '../components/submissions/BulkSendModal';
import SupplierMultiSelect from '../components/ui/SupplierMultiSelect';
import SubmissionDetailModal from '../components/submissions/SubmissionDetailModal';
import DeadlineUpdateModal from '../components/submissions/DeadlineUpdateModal';
import ReminderWarningModal from '../components/submissions/ReminderWarningModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// **NEU: ANSWERS MODAL IMPORT**
import SubmissionAnswersModal from '../components/submissions/SubmissionAnswersModal';

const Submissions: React.FC = () => {
  // **HOOKS**
  const { showSuccess, showWarning, showError } = useToast();
  
  // **STATE MANAGEMENT**
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // **STATISTICS**
  const [stats, setStats] = useState<SubmissionStats>({
    total: 0,
    sent: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    notSentThisYear: 0,
    remindersDue: 0,
    completionRate: 0,
    averageCompletionTime: 0
  });
  
  const [deadlineStats, setDeadlineStats] = useState<DeadlineStats>({
    dueToday: 0,
    dueTomorrow: 0,
    dueThisWeek: 0,
    dueNextWeek: 0,
    overdue: 0
  });

  // **PAGINATION & FILTERS**
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  const [filters, setFilters] = useState<SubmissionFilters>({
    search: '',
    status: 'all',
    assignedTo: 'all',
    templateId: 'all',
    supplierId: [],
    yearGroup: new Date().getFullYear(),
    overdue: false,
    deadlineFrom: '',
    deadlineTo: '',
    showDeleted: false
  });

  // **SORTING**
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // **MODALS**
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [showReminderWarningModal, setShowReminderWarningModal] = useState(false);
  const [reminderWarningData, setReminderWarningData] = useState<{
    submission: Submission;
    daysSinceLastReminder: number;
    lastReminderDate: Date;
  } | null>(null);
  const [deadlineModalData, setDeadlineModalData] = useState<{
    submissionId: string;
    currentDeadline: string;
    supplierName: string;
  } | null>(null);

  // **NEUE DELETE MODAL STATES**
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<{
    type: 'single' | 'bulk';
    submissionId?: string;
    supplierName?: string;
    count?: number;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // **SELECTION**
  const [selectedSubmissions, setSelectedSubmissions] = useState<string[]>([]);

  // **GRUPPIERUNG: State für ein-/ausgeklappte Gruppen (standardmäßig eingeklappt)**

  // **ACTIONS**
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // **NEU: ANSWERS MODAL STATE**
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedSubmissionForAnswers, setSelectedSubmissionForAnswers] = useState<string | null>(null);

  // **DEBOUNCED SEARCH STATE**
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [wasSearchFocused, setWasSearchFocused] = useState(false);

  // **LOAD DATA - getrennte Funktionen für bessere Performance**
  const loadSubmissions = useCallback(async (customSearch?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const searchTerm = customSearch !== undefined ? customSearch : debouncedSearch;
      const effectiveFilters = { ...filters, search: searchTerm };
      
      const submissionsResponse = await getSubmissions({
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        ...effectiveFilters,
        // Nur senden wenn Suppliers ausgewählt sind
        supplierId: Array.isArray(effectiveFilters.supplierId) && effectiveFilters.supplierId.length > 0 
          ? effectiveFilters.supplierId 
          : 'all'
      });

      console.log('📥 Submissions Response:', {
        totalReceived: submissionsResponse.data.submissions?.length || 0,
        submissions: submissionsResponse.data.submissions,
        pagination: submissionsResponse.data.pagination,
        groupInfo: submissionsResponse.data.groupInfo
      });

      setSubmissions(submissionsResponse.data.submissions || []);
      setTotalPages(submissionsResponse.data.pagination?.pages || 0);
      setTotalItems(submissionsResponse.data.pagination?.total || 0);
      
      console.log('✅ Submissions state updated:', {
        count: submissionsResponse.data.submissions?.length || 0,
        hasGroupHeaders: submissionsResponse.data.submissions?.some((s: any) => s.isGroupHeader)
      });
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError('Fehler beim Laden der Submissions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, sortBy, sortOrder, debouncedSearch]);

  const loadStaticData = useCallback(async () => {
    try {
      // **OPTIMIZED: Load only essential data for filters**
      const [templatesResponse, suppliersResponse] = await Promise.all([
        getQuestionnaireTemplates(),
        getSuppliers({ 
          limit: 50, // Reduziert von 1000 auf 50 für bessere Performance
          sortBy: 'name',
          sortOrder: 'asc',
          isActive: 'true' // Nur aktive Suppliers für Filter
        })
      ]);
      
      setTemplates(templatesResponse.data || []);
      setSuppliers(suppliersResponse.data.suppliers || []);
      
      console.log('📊 Static data loaded:', {
        templates: templatesResponse.data?.length || 0,
        suppliers: suppliersResponse.data.suppliers?.length || 0
      });
    } catch (err) {
      console.error('Error loading static data:', err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const [statsResponse, deadlineStatsResponse] = await Promise.all([
        getSubmissionStats(filters.yearGroup),
        getDeadlineStats()
      ]);
      
      setStats(statsResponse.data || stats);
      setDeadlineStats(deadlineStatsResponse.data || deadlineStats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [filters.yearGroup]);

  // **COMBINED LOAD FUNCTION - Only for initial load**
  const loadInitialData = useCallback(async () => {
    await Promise.all([
      loadSubmissions(),
      loadStaticData(),
      loadStats()
    ]);
  }, [loadSubmissions, loadStaticData, loadStats]);

  // **REFRESH FUNCTION - Only reload submissions and stats, NOT suppliers**
  const refreshSubmissions = useCallback(async () => {
    await Promise.all([
      loadSubmissions(),
      loadStats()
    ]);
  }, [loadSubmissions, loadStats]);

  // **INITIAL LOAD - Nur einmal beim Mount**
  useEffect(() => {
    loadInitialData(); // Lädt alles beim ersten Mal
  }, [loadInitialData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  // **SUBMISSIONS RELOAD - bei allen Filter/Sortierung/Pagination Änderungen**
  useEffect(() => {
    loadSubmissions(debouncedSearch);
  }, [currentPage, itemsPerPage, sortBy, sortOrder, filters.status, filters.supplierId, filters.templateId, filters.yearGroup, filters.showDeleted, debouncedSearch]);

  // **RESTORE FOCUS AFTER LOADING**
  useEffect(() => {
    if (!loading && wasSearchFocused && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [loading, wasSearchFocused]);

  // **FILTER HANDLERS**
  const handleFilterChange = (key: keyof SubmissionFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedSubmissions([]);
  };

  // **STABILIZED SEARCH HANDLERS**
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setCurrentPage(1);
    setSelectedSubmissions([]);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setWasSearchFocused(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setWasSearchFocused(false);
  }, []);

  const handleSupplierFilterChange = (supplierIds: string[]) => {
    setFilters(prev => ({ ...prev, supplierId: supplierIds }));
    setCurrentPage(1);
    setSelectedSubmissions([]);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // **GRUPPIERUNG: Toggle-Funktion für Gruppen (standardmäßig eingeklappt)**
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  const toggleGroupCollapse = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      assignedTo: 'all',
      templateId: 'all',
      supplierId: [],
      yearGroup: new Date().getFullYear(),
      overdue: false,
      deadlineFrom: '',
      deadlineTo: '',
      showDeleted: false
    });
    setCurrentPage(1);
    setSelectedSubmissions([]);
  };

  // **SELECTION HANDLERS** 
  const handleSelectSubmission = (submissionId: string) => {
    setSelectedSubmissions(prev => 
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const handleSelectAll = () => {
    // Filter out group headers - only select actual submissions
    const allIds = actualSubmissions.map(sub => sub._id);
    setSelectedSubmissions(prev => 
      prev.length === allIds.length ? [] : allIds
    );
  };

  // **BULK ACTIONS**
  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedSubmissions.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedSubmissions.map(id => 
          updateSubmissionStatus(id, newStatus, 'Bulk-Update')
        )
      );
      setSelectedSubmissions([]);
      refreshSubmissions();
    } catch (error) {
      console.error('Error updating bulk status:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSubmissions.length === 0) return;
    
    setDeleteModalData({
      type: 'bulk',
      count: selectedSubmissions.length
    });
    setShowDeleteModal(true);
  };

  const handleBulkReminder = async () => {
    if (selectedSubmissions.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedSubmissions.map(id => sendManualReminder(id))
      );
      setSelectedSubmissions([]);
      refreshSubmissions();
    } catch (error) {
      console.error('Error sending reminders:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // **SINGLE REMINDER WITH TOAST AND WARNING**
  const handleSingleReminder = async (submission: Submission) => {
    try {
      // **CHECK IF REMINDER WAS SENT WITHIN LAST WEEK**
      const lastReminderDate = submission.lastReminderAt ? new Date(submission.lastReminderAt) : null;
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      if (lastReminderDate && lastReminderDate > oneWeekAgo) {
        const daysSinceLastReminder = Math.ceil((new Date().getTime() - lastReminderDate.getTime()) / (1000 * 3600 * 24));
        
        // **SHOW CUSTOM MODAL INSTEAD OF WINDOW.CONFIRM**
        setReminderWarningData({
          submission,
          daysSinceLastReminder,
          lastReminderDate
        });
        setShowReminderWarningModal(true);
        return;
      }
      
      // **SEND REMINDER DIRECTLY IF NO WARNING NEEDED**
      await sendReminderDirectly(submission);
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError(
        'Fehler beim Senden der Erinnerung',
        'Die Erinnerung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
      );
    }
  };

  // **HELPER FUNCTION TO SEND REMINDER**
  const sendReminderDirectly = async (submission: Submission) => {
    try {
      await sendManualReminder(submission._id);
      
      // **SHOW SUCCESS TOAST**
      showSuccess(
        'Erinnerung gesendet',
        `Erinnerung wurde erfolgreich an ${submission.supplierId.name} gesendet.`
      );
      
      // **RELOAD DATA TO UPDATE lastReminderAt**
      refreshSubmissions();
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError(
        'Fehler beim Senden der Erinnerung',
        'Die Erinnerung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
      );
    }
  };

  // **HANDLE CONFIRMED REMINDER FROM MODAL**
  const handleConfirmedReminder = () => {
    if (reminderWarningData) {
      sendReminderDirectly(reminderWarningData.submission);
      setReminderWarningData(null);
    }
  };

  // **SINGLE DELETE**
  const handleDeleteSubmission = async (submissionId: string, supplierName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    setDeleteModalData({
      type: 'single',
      submissionId,
      supplierName
    });
    setShowDeleteModal(true);
  };

  // **NEUE DELETE CONFIRM FUNKTION**
  const handleDeleteConfirm = async () => {
    if (!deleteModalData) return;

    try {
      setDeleteLoading(true);
      
      if (deleteModalData.type === 'bulk') {
        await Promise.all(
          selectedSubmissions.map(id => deleteSubmission(id))
        );
        setSelectedSubmissions([]);
      } else if (deleteModalData.type === 'single' && deleteModalData.submissionId) {
        await deleteSubmission(deleteModalData.submissionId);
      }
      
      // Nach dem Löschen: Submissions und Stats neu laden
      await Promise.all([
        loadSubmissions(),
        loadStats()
      ]);
      setShowDeleteModal(false);
      setDeleteModalData(null);
      
    } catch (error) {
      console.error('Error deleting submission(s):', error);
      setError('Fehler beim Löschen der Submission(s)');
    } finally {
      setDeleteLoading(false);
    }
  };

  // **DELETE MODAL SCHLIESSEN**
  const handleCloseDeleteModal = () => {
    if (deleteLoading) return; // Prevent closing while deleting
    setShowDeleteModal(false);
    setDeleteModalData(null);
  };

  // **DELETE MODAL TITLE UND MESSAGE**
  const getDeleteModalContent = () => {
    if (!deleteModalData) return { title: '', message: '' };

    if (deleteModalData.type === 'bulk') {
      return {
        title: 'Submissions löschen',
        message: `Möchten Sie ${deleteModalData.count} Submission${deleteModalData.count === 1 ? '' : 's'} wirklich löschen?`
      };
    } else {
      return {
        title: 'Submission löschen',
        message: `Möchten Sie die Submission für "${deleteModalData.supplierName}" wirklich löschen?`
      };
    }
  };

  // **EXPORT**
  const handleExport = async (format: 'xlsx' | 'pdf') => {
    setExportLoading(true);
    try {
      const response = await exportSubmissions(format, filters);
      
      const blob = new Blob([response.data], {
        type: format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `submissions-${filters.yearGroup}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      setError('Fehler beim Exportieren');
    } finally {
      setExportLoading(false);
    }
  };

  // **MODAL HANDLERS**
  const handleShowDetail = (submissionId: string) => {
    setSelectedSubmissionId(submissionId);
    setShowDetailModal(true);
  };

  const handleShowDeadlineModal = (submission: Submission) => {
    setDeadlineModalData({
      submissionId: submission._id,
      currentDeadline: submission.deadline,
      supplierName: submission.supplierId?.name || 'Unbekannt'
    });
    setShowDeadlineModal(true);
  };

  // **NEU: ANTWORTEN ANZEIGEN HANDLER**
  const handleViewAnswers = useCallback((submission: Submission, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedSubmissionForAnswers(submission._id);
    setShowAnswersModal(true);
  }, []);

  const handleCloseAnswersModal = useCallback(() => {
    setShowAnswersModal(false);
    setSelectedSubmissionForAnswers(null);
  }, []);

  // **HELPER: CAN SHOW ANSWERS**
  const canShowAnswers = useCallback((submission: Submission) => {
    return submission.status === 'in_progress' || 
           submission.status === 'completed' || 
           (submission.progressPercentage != null && submission.progressPercentage > 0);
  }, []);

  // **COMPUTED VALUES**
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getDaysUntilDeadline = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: { label: 'Abgeschlossen', class: 'bg-green-100 text-green-800' },
      in_progress: { label: 'In Bearbeitung', class: 'bg-blue-100 text-blue-800' },
      sent: { label: 'Versendet', class: 'bg-yellow-100 text-yellow-800' },
      overdue: { label: 'Überfällig', class: 'bg-red-100 text-red-800' },
      draft: { label: 'Entwurf', class: 'bg-gray-100 text-gray-800' }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <FontAwesomeIcon icon={faSort} className="text-gray-400" />;
    return <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="text-gray-600" />;
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Submissions werden geladen..." />;
  }

  // Helper: Get actual submissions (without group headers) for selection logic
  const actualSubmissions = submissions.filter(sub => !sub.isGroupHeader);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* **KOMPAKTE TOOLBAR** */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* **SUCHE** */}
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-2.5 text-gray-400 text-sm" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Submissions durchsuchen..."
              value={filters.search}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm"
            />
          </div>
          
          {/* **FILTER UND AKTIONEN** */}
          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="all">Alle Status</option>
              <option value="draft">Entwurf</option>
              <option value="sent">Versendet</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="completed">Abgeschlossen</option>
              <option value="overdue">Überfällig</option>
              <option value="expired">Abgelaufen</option>
            </select>

            {/* **FILTER FÜR GELÖSCHTE SUBMISSIONS** */}
            <select
              value={filters.showDeleted ? 'deleted' : 'active'}
              onChange={(e) => handleFilterChange('showDeleted', e.target.value === 'deleted')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[120px]"
            >
              <option value="active">Aktiv</option>
              <option value="deleted">Gelöscht</option>
            </select>
            
            <select
              value={filters.templateId}
              onChange={(e) => handleFilterChange('templateId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[150px]"
            >
              <option value="all">Alle Templates</option>
              {templates.map(template => (
                <option key={template._id} value={template._id}>
                  {template.title.de}
                </option>
              ))}
            </select>
            
            <SupplierMultiSelect
              suppliers={suppliers}
              selectedSupplierIds={Array.isArray(filters.supplierId) ? filters.supplierId : []}
              onChange={handleSupplierFilterChange}
              placeholder="Alle Lieferanten"
              className="min-w-[180px]"
            />
            
            <select
              value={filters.yearGroup}
              onChange={(e) => handleFilterChange('yearGroup', parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent bg-white text-sm min-w-[80px]"
            >
              {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors text-sm"
              title="Filter zurücksetzen"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
            
            <button
              onClick={() => handleExport('xlsx')}
              disabled={exportLoading}
              className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              <FontAwesomeIcon icon={exportLoading ? faSpinner : faFileExcel} className={`mr-1.5 text-xs ${exportLoading ? 'animate-spin' : ''}`} />
              Export
            </button>
            
            <button
              onClick={() => setShowBulkSendModal(true)}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
            >
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1.5 text-xs" />
              Bulk-Versendung
            </button>
          </div>
        </div>
      </div>

      {/* **ERROR MESSAGE** */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.15 }}
        className="px-4 pt-4"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-center justify-between text-sm">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
        )}
      </motion.div>

      {/* **STATISTICS CARDS** */}
      <div className="p-4 pb-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Gesamt</p>
                <p className="text-lg font-bold text-blue-900">{stats.total}</p>
              </div>
              <div className="text-blue-500 text-sm">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600 font-medium">Versendet</p>
                <p className="text-lg font-bold text-yellow-900">{stats.sent}</p>
              </div>
              <div className="text-yellow-500 text-sm">
                <FontAwesomeIcon icon={faPaperPlane} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">In Bearbeitung</p>
                <p className="text-lg font-bold text-purple-900">{stats.inProgress}</p>
              </div>
              <div className="text-purple-500 text-sm">
                <FontAwesomeIcon icon={faEdit} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Abgeschlossen</p>
                <p className="text-lg font-bold text-green-900">{stats.completed}</p>
              </div>
              <div className="text-green-500 text-sm">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 font-medium">Überfällig</p>
                <p className="text-lg font-bold text-red-900">{stats.overdue}</p>
              </div>
              <div className="text-red-500 text-sm">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">Erinnerungen</p>
                <p className="text-lg font-bold text-orange-900">{stats.remindersDue}</p>
              </div>
              <div className="text-orange-500 text-sm">
                <FontAwesomeIcon icon={faBell} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* **VERBESSERTE BULK ACTIONS** */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mx-4 mb-4"
      >
        {selectedSubmissions.length > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {selectedSubmissions.length}
                  </div>
                  <span className="text-sm font-semibold text-purple-900">
                    {selectedSubmissions.length === 1 ? 'Eintrag ausgewählt' : 'Einträge ausgewählt'}
                  </span>
                </div>
                
                <div className="h-4 w-px bg-purple-300"></div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleBulkStatusUpdate('completed')}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2 py-1 bg-green-500 text-white rounded text-xs font-medium hover:bg-green-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                    Abschließen
                  </button>
                  
                  <button
                    onClick={handleBulkReminder}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                    Erinnerung
                  </button>
                  
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2 py-1 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Löschen
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedSubmissions([])}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-200 rounded-full p-1 transition-all duration-150"
                title="Auswahl aufheben"
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* **CONTENT BEREICH - LISTE MIT RUNDEN CHECKBOXEN** */}
      <div className="p-4">
        {submissions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <div className="text-gray-400 mb-4">
              <FontAwesomeIcon icon={faClipboardList} className="text-4xl mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {filters.search || filters.status !== 'all' 
                  ? 'Keine Submissions gefunden' 
                  : 'Noch keine Submissions vorhanden'}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {filters.search || filters.status !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Erstellen Sie Ihre erste Submission über die Bulk-Versendung'}
              </p>
            </div>
            {!filters.search && filters.status === 'all' && (
              <button
                onClick={() => setShowBulkSendModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="mr-1.5 text-xs" />
                Fragebogen versenden
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* **TABLE HEADER MIT LINKSGEBÜNDETEM LAYOUT** */}
            <div className="bg-gray-50 border-b border-gray-200 px-2 py-3">
              <div className="grid grid-cols-12 gap-3 items-center text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <div className="col-span-1 flex justify-start pl-2">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selectedSubmissions.length === actualSubmissions.length && actualSubmissions.length > 0}
                      onChange={handleSelectAll}
                      className="sr-only"
                      id="select-all"
                    />
                    <label
                      htmlFor="select-all"
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        selectedSubmissions.length === actualSubmissions.length && actualSubmissions.length > 0
                          ? 'bg-purple-500 border-purple-500'
                          : 'border-gray-300 hover:border-purple-400'
                      }`}
                    >
                      {selectedSubmissions.length === actualSubmissions.length && actualSubmissions.length > 0 && (
                        <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                      )}
                    </label>
                  </div>
                </div>
                <div className="col-span-2 text-left pl-2">
                  <button
                    onClick={() => handleSort('supplierId.name')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Lieferant {getSortIcon('supplierId.name')}
                  </button>
                </div>
                <div className="col-span-2 text-left pl-2">
                  <button
                    onClick={() => handleSort('questionnaireId.title.de')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Template {getSortIcon('questionnaireId.title.de')}
                  </button>
                </div>
                <div className="col-span-1 text-left pl-2">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Status {getSortIcon('status')}
                  </button>
                </div>
                <div className="col-span-1 text-left pl-2">
                  <button
                    onClick={() => handleSort('deadline')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Deadline {getSortIcon('deadline')}
                  </button>
                </div>
                <div className="col-span-2 text-left pl-2">
                  <button
                    onClick={() => handleSort('progressPercentage')}
                    className="flex items-center gap-1 hover:text-gray-900"
                  >
                    Fortschritt {getSortIcon('progressPercentage')}
                  </button>
                </div>
                <div className="col-span-3 text-right pr-4">
                  Aktionen
                </div>
              </div>
            </div>

            {/* **TABLE BODY MIT LINKSGEBÜNDETEM LAYOUT UND GRUPPIERUNG** */}
            <div className="divide-y divide-gray-100">
              {submissions.map((submission, index) => {
                // Handle group headers
                if (submission.isGroupHeader) {
                  const isExpanded = expandedGroups.has(submission._id);
                  const stats = submission.groupStats || { total: 0, completed: 0, overdue: 0, inProgress: 0, sent: 0 };
                  
                  return (
                    <div
                      key={submission._id}
                      className="bg-gray-100 px-4 py-3 border-l-4 border-blue-400 cursor-pointer hover:bg-gray-150 transition-colors"
                      onClick={() => toggleGroupCollapse(submission._id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon 
                            icon={isExpanded ? faChevronDown : faChevronRight} 
                            className="text-blue-600 w-4 h-4" 
                          />
                          <FontAwesomeIcon icon={faClipboardList} className="text-blue-600" />
                          <span className="font-semibold text-gray-800">
                            Lieferantengruppe: {submission.groupName}
                          </span>
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                            {submission.submissionCount} Fragebogen
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* Status-Statistiken */}
                          <div className="flex items-center gap-2 text-xs">
                            {stats.completed > 0 && (
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                                <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
                                {stats.completed} fertig
                              </span>
                            )}
                            {stats.overdue > 0 && (
                              <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full flex items-center gap-1">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                                {stats.overdue} überfällig
                              </span>
                            )}
                            {stats.inProgress > 0 && (
                              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex items-center gap-1">
                                <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                                {stats.inProgress} in Bearbeitung
                              </span>
                            )}
                            {stats.sent > 0 && stats.overdue === 0 && stats.completed === 0 && stats.inProgress === 0 && (
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center gap-1">
                                <FontAwesomeIcon icon={faPaperPlane} className="w-3 h-3" />
                                {stats.sent} versendet
                              </span>
                            )}
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            {isExpanded ? 'Klicken zum Einklappen' : 'Klicken zum Ausklappen'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Handle regular submissions
                const daysUntil = getDaysUntilDeadline(submission.deadline);
                const isSelected = selectedSubmissions.includes(submission._id);
                
                // Check if this submission belongs to a collapsed group
                // Look back to find the group header this submission belongs to
                let belongsToExpandedGroup = false;
                for (let i = index - 1; i >= 0; i--) {
                  const prevItem = submissions[i];
                  if (prevItem.isGroupHeader) {
                    belongsToExpandedGroup = expandedGroups.has(prevItem._id);
                    break;
                  }
                }
                
                // Don't render if belongs to collapsed group
                if (!belongsToExpandedGroup) {
                  return null;
                }
                
                return (
                  <motion.div
                    key={submission._id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.01 }}
                    className={`grid grid-cols-12 gap-3 items-center px-2 py-4 hover:bg-gray-50 transition-all duration-150 text-sm ${
                      isSelected ? 'bg-purple-50 border-l-4 border-purple-400' : ''
                    } ${
                      submission.deletedAt ? 'opacity-60 bg-red-50' : ''
                    }`}
                  >
                    {/* **RUNDE CHECKBOX GANZ LINKS** */}
                    <div className="col-span-1 flex justify-start pl-2">
                      <div 
                        className="relative cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSubmission(submission._id);
                        }}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                          isSelected
                            ? 'bg-purple-500 border-purple-500'
                            : 'border-gray-300 hover:border-purple-400'
                        }`}>
                          {isSelected && (
                            <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* **KLICKBARER CONTENT BEREICH** */}
                    <div 
                      className="col-span-11 grid grid-cols-11 gap-3 items-center cursor-pointer"
                      onClick={() => handleShowDetail(submission._id)}
                    >
                      {/* **SUPPLIER - 2 Spalten, linksgebündete Darstellung** */}
                      <div className="col-span-2 text-left pl-2">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 truncate">
                              {submission.supplierId?.name || 'Unbekannt'}
                              {submission.deletedAt && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                                  Gelöscht
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {submission.supplierId?.email}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* **TEMPLATE - 2 Spalten, linksgebündete Darstellung** */}
                      <div className="col-span-2 text-left pl-2">
                        <div className="text-gray-900 text-xs leading-tight line-clamp-2">
                          {submission.questionnaireId?.title?.de || 'Unbekannt'}
                        </div>
                      </div>

                      {/* **STATUS - 1 Spalte, linksgebündete Darstellung** */}
                      <div className="col-span-1 text-left pl-2">
                        {getStatusBadge(submission.status)}
                      </div>

                      {/* **DEADLINE - 1 Spalte, nebeneinander** */}
                      <div className="col-span-1 text-left pl-2">
                        <div className="flex items-center gap-2">
                          <div className="text-gray-900 font-medium text-xs whitespace-nowrap">
                            {formatDate(submission.deadline)}
                          </div>
                          <div className={`text-xs font-medium whitespace-nowrap ${
                            daysUntil < 0 ? 'text-red-600' : 
                            daysUntil <= 2 ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {daysUntil < 0 
                              ? `${Math.abs(daysUntil)}d überfällig`
                              : `${daysUntil}d verbleibend`
                            }
                          </div>
                        </div>
                      </div>

                      {/* **PROGRESS - 2 Spalten, nebeneinander** */}
                      <div className="col-span-2 text-left pl-2">
                        <div className="flex items-center gap-3">
                          {/* Progress Bar */}
                          <div className="flex-1 bg-gray-200 rounded-full h-2.5 min-w-[60px]">
                            <div 
                              className={`h-2.5 rounded-full transition-all duration-300 ${
                                submission.progressPercentage >= 80 ? 'bg-green-500' :
                                submission.progressPercentage >= 50 ? 'bg-yellow-500' :
                                submission.progressPercentage >= 20 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${submission.progressPercentage}%` }}
                            ></div>
                          </div>
                          
                          {/* Percentage und Status nebeneinander */}
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-xs text-gray-600 font-medium">
                              {submission.progressPercentage}%
                            </span>
                            <div className="flex items-center gap-1">
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                submission.progressPercentage >= 80 ? 'bg-green-500' :
                                submission.progressPercentage >= 50 ? 'bg-yellow-500' :
                                submission.progressPercentage >= 20 ? 'bg-orange-500' : 'bg-red-500'
                              }`}></span>
                              <span className="text-xs text-gray-500">
                                {submission.progressPercentage >= 80 ? 'Fast fertig' :
                                 submission.progressPercentage >= 50 ? 'In Bearbeitung' :
                                 submission.progressPercentage >= 20 ? 'Begonnen' : 'Nicht begonnen'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* **AKTIONEN - 3 Spalten rechtsgebündet** */}
                      <div className="col-span-3 text-right pr-4">
                        <div className="flex items-center justify-end gap-1 flex-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowDetail(submission._id);
                            }}
                            className="px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 font-medium transition-colors whitespace-nowrap"
                            title="Details"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Details
                          </button>

                          {/* **NEU: ANTWORTEN ANZEIGEN BUTTON** */}
                          {canShowAnswers(submission) && (
                            <button
                              onClick={(e) => handleViewAnswers(submission, e)}
                              className="px-2 py-1.5 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium transition-colors whitespace-nowrap"
                              title="Antworten anzeigen"
                            >
                              <FontAwesomeIcon icon={faClipboardList} className="mr-1" />
                              Antworten
                            </button>
                          )}
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowDeadlineModal(submission);
                            }}
                            className="px-2 py-1.5 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100 font-medium transition-colors whitespace-nowrap"
                            title="Deadline"
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                            Deadline
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSingleReminder(submission);
                            }}
                            disabled={submission.status === 'completed'}
                            className="px-2 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                            title="Erinnerung"
                          >
                            <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                            Erinnerung
                          </button>
                          
                          <button
                            onClick={(e) => handleDeleteSubmission(submission._id, submission.supplierId?.name || 'Unbekannt', e)}
                            className="px-2 py-1.5 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium transition-colors whitespace-nowrap"
                            title="Löschen"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* **PAGINATION** */}
      {totalPages > 1 && (
        <div className="px-4 pb-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Seite {currentPage} von {totalPages} ({totalItems} Einträge)
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                
                {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                  const pageNum = Math.max(1, currentPage - 2) + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        currentPage === pageNum
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* **MODALS** */}
      {showBulkSendModal && (
        <BulkSendModal
          templates={templates}
          onClose={() => setShowBulkSendModal(false)}
          onSuccess={() => {
            setShowBulkSendModal(false);
            refreshSubmissions();
          }}
          onError={(message: string) => {
            setError(message);
          }}
        />
      )}
      
      {showDetailModal && selectedSubmissionId && (
        <SubmissionDetailModal
          submissionId={selectedSubmissionId}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSubmissionId(null);
          }}
          onUpdate={refreshSubmissions}
        />
      )}
      
      {showDeadlineModal && deadlineModalData && (
        <DeadlineUpdateModal
          submissionId={deadlineModalData.submissionId}
          currentDeadline={deadlineModalData.currentDeadline}
          supplierName={deadlineModalData.supplierName}
          onClose={() => {
            setShowDeadlineModal(false);
            setDeadlineModalData(null);
          }}
          onUpdate={refreshSubmissions}
        />
      )}

      {/* **NEU: ANSWERS MODAL** */}
      {showAnswersModal && selectedSubmissionForAnswers && (
        <SubmissionAnswersModal
          submissionId={selectedSubmissionForAnswers}
          onClose={handleCloseAnswersModal}
        />
      )}

      {/* **DELETE MODAL** */}
      {showDeleteModal && deleteModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-xl mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                {(() => {
                  const { title } = getDeleteModalContent();
                  return title;
                })()}
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              {(() => {
                const { message } = getDeleteModalContent();
                return message;
              })()}
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleteLoading}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {deleteLoading && (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                )}
                Löschen
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reminder Warning Modal */}
      <ReminderWarningModal
        isOpen={showReminderWarningModal}
        onClose={() => {
          setShowReminderWarningModal(false);
          setReminderWarningData(null);
        }}
        onConfirm={handleConfirmedReminder}
        supplierName={reminderWarningData?.submission.supplierId.name || ''}
        daysSinceLastReminder={reminderWarningData?.daysSinceLastReminder || 0}
        lastReminderDate={reminderWarningData?.lastReminderDate || new Date()}
      />
    </div>
  );
};

export default Submissions;